import { NewOrder_OrderItemFieldsFragment } from '@/types';
import {
  UpdateQuantityData,
  UpdateQuantityMetadata,
  VariantPackDetails,
} from '@/contexts/new-order-actions-context-types';
import { useReducer } from 'react';
import { VariantPackEventPayloads } from '@/utils/ads';

export interface QuantityMapType {
  [variantPackUUID: string]: number;
}

export type EventPayloadMap = Partial<Record<string, VariantPackEventPayloads>>;

export interface VariantPackDetailMap {
  [variantPackUUID: string]: VariantPackDetails | undefined;
}

export interface QuantityStateType {
  eventPayloadMap: EventPayloadMap;
  localQuantityMap: QuantityMapType;
  savedQuantityMap: QuantityMapType;
  variantPackDetailMap: VariantPackDetailMap;
}

export const initialState: QuantityStateType = {
  eventPayloadMap: {},
  localQuantityMap: {},
  savedQuantityMap: {},
  variantPackDetailMap: {},
};

export enum OrderQuantityActions {
  CLEAR = 'CLEAR',
  HYDRATE = 'HYDRATE',
  UPDATE_QUANTITY = 'UPDATE_QUANTITY',
}

interface ClearAction {
  payload?: {
    clearSaved?: boolean;
    variantPackUUIDs?: string[];
  };
  type: OrderQuantityActions.CLEAR;
}

interface HydrateAction {
  payload: {
    items: NewOrder_OrderItemFieldsFragment[];
  };
  type: OrderQuantityActions.HYDRATE;
}

export interface DispatchUpdateQuantityActionArgType {
  payload: UpdateQuantityData & UpdateQuantityMetadata;
}

type UpdateQuantityAction = DispatchUpdateQuantityActionArgType & {
  type: OrderQuantityActions.UPDATE_QUANTITY;
};

export type QuantityReducerAction =
  | ClearAction
  | HydrateAction
  | UpdateQuantityAction;

export const getVisibleQuantity = (
  state: QuantityStateType,
  variantPackUUID: string,
) => {
  return (
    state.localQuantityMap[variantPackUUID] ??
    state.savedQuantityMap[variantPackUUID] ??
    0
  );
};

// Preforms the minimum required updates for cart details
export const createOrderItemsInput = (state: QuantityStateType) => {
  const toSave = Object.keys(state.localQuantityMap).reduce(
    (acc: QuantityMapType, variantPackUUID: string) => {
      const updatedValue = state.localQuantityMap[variantPackUUID];
      const savedValue = state.savedQuantityMap[variantPackUUID];

      // Save the quantity if it's being added for the first time, but not if the quantity is 0
      const isAdding = !savedValue && updatedValue !== 0;

      // Save the quantity if it's changed since the last time it's been saved
      const isChanging = savedValue && updatedValue !== savedValue;

      if (isAdding || isChanging) {
        acc[variantPackUUID] = updatedValue;
      }
      return acc;
    },
    {},
  );

  return toSave;
};

interface HydrateAccumulator {
  savedQuantityMap: QuantityMapType;
  variantPackDetailMap: VariantPackDetailMap;
}

export const quantityReducer = (
  state: QuantityStateType,
  action: QuantityReducerAction,
): QuantityStateType => {
  switch (action.type) {
    case OrderQuantityActions.HYDRATE: {
      const savedValues = action.payload.items.reduce(
        (
          acc: HydrateAccumulator,
          orderItem: NewOrder_OrderItemFieldsFragment,
        ) => {
          const orderItemPrices = orderItem.order_item_prices;
          if (
            orderItemPrices &&
            typeof orderItemPrices.pack_quantity_at_order === 'number'
          ) {
            acc.savedQuantityMap[orderItem.variants_pack!.uuid] =
              orderItemPrices.pack_quantity_at_order;
            acc.variantPackDetailMap[orderItem.variants_pack!.uuid] = {
              itemDisplayName: orderItem.restaurant_display_name,
              metadata: orderItem.variants_pack!.metadata,
            };
          }
          return acc;
        },
        {
          savedQuantityMap: {},
          variantPackDetailMap: {},
        },
      );
      return {
        ...state,
        savedQuantityMap: savedValues.savedQuantityMap,
        variantPackDetailMap: {
          ...state.variantPackDetailMap,
          ...savedValues.variantPackDetailMap,
        },
      };
    }
    case OrderQuantityActions.UPDATE_QUANTITY:
      // NOTE: use dispatchQuantityUpdateAction instead of invoking this action directly
      const {
        eventPayloads,
        newQuantity,
        variantPackUUID,
        variantPackDetails,
      } = action.payload;
      if (newQuantity < 0) {
        return state;
      }

      const nextUpdatedEventMap = eventPayloads
        ? {
            ...state.eventPayloadMap,
            [variantPackUUID]: eventPayloads,
          }
        : state.eventPayloadMap;

      const nextQuantityMap = {
        ...state.localQuantityMap,
        [variantPackUUID]: newQuantity,
      };

      let nextVariantPackDetailMap = state.variantPackDetailMap;
      if (newQuantity === 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [variantPackUUID]: __, ...variantPackDetailMap } =
          state.variantPackDetailMap;
        nextVariantPackDetailMap = variantPackDetailMap;
      } else {
        nextVariantPackDetailMap = {
          ...state.variantPackDetailMap,
          [variantPackUUID]: variantPackDetails,
        };
      }

      return {
        ...state,
        eventPayloadMap: nextUpdatedEventMap,
        localQuantityMap: nextQuantityMap,
        variantPackDetailMap: nextVariantPackDetailMap,
      };
    case OrderQuantityActions.CLEAR:
      if (action.payload?.clearSaved) {
        return initialState;
      }

      const vpsToClear = action.payload?.variantPackUUIDs;
      if (vpsToClear) {
        const nextLocalQuantityMap = {
          ...state.localQuantityMap,
        };
        const nextVariantPackDetailMap = {
          ...state.variantPackDetailMap,
        };
        for (const vpUUID of vpsToClear) {
          delete nextLocalQuantityMap[vpUUID];
          delete nextVariantPackDetailMap[vpUUID];
        }
        return {
          ...state,
          localQuantityMap: nextLocalQuantityMap,
          variantPackDetailMap: nextVariantPackDetailMap,
        };
      }

      return {
        ...state,
        localQuantityMap: {},
      };
    default:
      return state || initialState;
  }
};

export const useQuantityReducer = (state: QuantityStateType = initialState) =>
  useReducer(quantityReducer, state);
